"use client";

import type { UIMessage } from "ai";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, HTMLAttributes } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      "group flex w-full gap-2 py-2.5",
      from === "user"
        ? "is-user flex-row-reverse items-end justify-start"
        : from === "assistant"
          ? "is-assistant items-start justify-start"
          : // System prompt表示しない
            "hidden",
      className,
    )}
    {...props}
  />
);

const messageContentVariants = cva(
  [
    "is-user:dark flex min-w-0 max-w-full flex-col gap-1.5 overflow-hidden text-[13px] leading-[1.65] break-words",
    "group-[.is-assistant]:gap-2.5",
  ].join(" "),
  {
    variants: {
      variant: {
        contained: [
          "group-[.is-user]:w-full group-[.is-user]:rounded-2xl group-[.is-user]:rounded-br-lg group-[.is-user]:border group-[.is-user]:border-border/60 group-[.is-user]:bg-secondary/80 group-[.is-user]:px-3.5 group-[.is-user]:py-2 group-[.is-user]:text-foreground group-[.is-user]:shadow-xs",
          "group-[.is-assistant]:w-full group-[.is-assistant]:text-foreground",
        ],
        flat: [
          "group-[.is-user]:w-full group-[.is-user]:rounded-2xl group-[.is-user]:rounded-br-lg group-[.is-user]:bg-secondary/80 group-[.is-user]:px-3.5 group-[.is-user]:py-2 group-[.is-user]:text-foreground",
          "group-[.is-assistant]:text-foreground",
        ],
      },
    },
    defaultVariants: {
      variant: "contained",
    },
  },
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof messageContentVariants>;

export const MessageContent = ({
  children,
  className,
  variant,
  ...props
}: MessageContentProps) => (
  <div
    className={cn(messageContentVariants({ variant, className }))}
    {...props}
  >
    {children}
  </div>
);

export type MessageAvatarProps = ComponentProps<typeof Avatar> & {
  src?: string | null;
  name?: string;
};

export const MessageAvatar = ({
  src,
  name,
  className,
  ...props
}: MessageAvatarProps) => (
  <Avatar className={cn("size-7 ring-1 ring-border/60", className)} {...props}>
    {src ? <AvatarImage alt="" className="mt-0 mb-0" src={src} /> : null}
    <AvatarFallback>{name?.slice(0, 2) || "ME"}</AvatarFallback>
  </Avatar>
);

export type MessageActionsProps = ComponentProps<"div">;

export const MessageActions = ({
  className,
  children,
  ...props
}: MessageActionsProps) => (
  <div
    className={cn("flex min-h-7 items-center gap-0.5", className)}
    {...props}
  >
    {children}
  </div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string;
  label?: string;
};

export const MessageAction = ({
  tooltip,
  children,
  label,
  variant = "ghost",
  size = "icon-sm",
  asChild = false,
  ...props
}: MessageActionProps) => {
  if (asChild) {
    if (!tooltip) {
      return children;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const button = (
    <Button size={size} type="button" variant={variant} {...props}>
      {children}
      <span className="sr-only">{label || tooltip}</span>
    </Button>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
};
